/**
 * Gingerbread! — Tor/Dark Web Index Operations Dashboard
 *
 * RESTRICTED — monadnockcyber.ai accounts only.
 * Tabs: Index Overview · Dump Sites · Subject Monitor · Full-Text Search
 */

import { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'

// ─── Access control ───────────────────────────────────────────────────────────

const ALLOWED_DOMAIN = 'monadnockcyber.ai'

// ─── Types ────────────────────────────────────────────────────────────────────

interface IndexStat { index_name: string; doc_count: number; size_mb: number; status: string }
interface RecentPage { url: string; domain: string; title: string; updated_on: string; content_preview: string }
interface CollectorStatus {
  last_crawl: string | null; last_lumen_sweep: string | null
  crawl_count: number; lumen_count: number
  next_crawl: string | null; next_lumen: string | null; crawler_active: boolean
}
interface DarkWebStats {
  total_indexed: number; indices: IndexStat[]; recent_pages: RecentPage[]
  collector: CollectorStatus; domains_sampled: string[]; timestamp: string
}
interface DumpSite { url: string; domain: string; title: string; category: string; dump_signals: string[]; updated_on: string; relevance_score: number }
interface DumpSiteResponse { total_dump_sites: number; sites: DumpSite[]; category_counts: Record<string, number> }
interface SubjectHit { url: string; domain: string; title: string; category: string; snippet: string; identifier_matched: string; confidence: string; updated_on: string; relevance_score: number }
interface BreachRecord { name: string; date: string; records?: number; data_exposed: string[]; description: string; severity: string; source: string; confidence: string }
interface PasteRecord { source: string; date?: string; title: string; email_count: number }
interface EmailBreachResult { email: string; breach_count: number; paste_count: number; risk_score: number; has_password_exposure: boolean; has_pii_exposure: boolean; breaches: BreachRecord[]; pastes: PasteRecord[]; error?: string }
interface SubjectMonitorResponse { subject: string; identifiers_searched: string[]; total_hits: number; dump_hits: number; results: SubjectHit[]; sites_searched: number; breach_results: EmailBreachResult[]; timestamp: string }
interface SearchResult { url: string; domain: string; title: string; snippet: string; updated_on: string; score: number }
interface LumenNotice { notice_id: number; title: string; notice_type: string; date_received: string; sender_name: string; recipient_name: string; action_taken: string; topics: string[]; url_count: number; snippet: string; score: number }
interface LumenSearchResponse { query: string; total_hits: number; results: LumenNotice[] }
interface LumenStatsResponse { total_notices: number; by_type: Record<string, number>; recent_notices: LumenNotice[]; timestamp: string }

// ─── Badge helpers ────────────────────────────────────────────────────────────

const CATEGORY_STYLES: Record<string, string> = {
  credential_dump: 'text-red-400 bg-red-950/40 border-red-500/30',
  pii_dump:        'text-orange-400 bg-orange-950/40 border-orange-500/30',
  stealer_logs:    'text-purple-400 bg-purple-950/40 border-purple-500/30',
  forum_market:    'text-yellow-400 bg-yellow-950/40 border-yellow-500/30',
  paste_site:      'text-blue-400 bg-blue-950/40 border-blue-500/30',
  general:         'text-dark-400 bg-dark-800 border-dark-700',
}
const CATEGORY_LABELS: Record<string, string> = {
  credential_dump: 'Credential Dump', pii_dump: 'PII / Fullz',
  stealer_logs: 'Stealer Logs', forum_market: 'Market', paste_site: 'Paste Site', general: 'General',
}
const NOTICE_TYPE_STYLES: Record<string, string> = {
  Defamation:         'text-red-400 bg-red-950/40 border-red-500/30',
  PrivateInformation: 'text-orange-400 bg-orange-950/40 border-orange-500/30',
  CourtOrder:         'text-purple-400 bg-purple-950/40 border-purple-500/30',
  GovernmentRequest:  'text-yellow-400 bg-yellow-950/40 border-yellow-500/30',
  DMCA:               'text-blue-400 bg-blue-950/40 border-blue-500/30',
  Trademark:          'text-cyan-400 bg-cyan-950/40 border-cyan-500/30',
}
const CONFIDENCE_STYLES: Record<string, string> = {
  HIGH:   'text-red-400 bg-red-950/40 border-red-500/40',
  MEDIUM: 'text-yellow-400 bg-yellow-950/40 border-yellow-500/40',
  LOW:    'text-dark-400 bg-dark-800 border-dark-700',
}

function CategoryBadge({ cat }: { cat: string }) {
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${CATEGORY_STYLES[cat] || CATEGORY_STYLES.general}`}>
      {CATEGORY_LABELS[cat] || cat}
    </span>
  )
}

type Tab = 'overview' | 'dumps' | 'monitor' | 'search' | 'lumen'

// ─── Component ────────────────────────────────────────────────────────────────

export default function TorIndexOps() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')

  // Access gate
  const isAllowed = user?.email?.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`)

  useEffect(() => {
    if (!authLoading && (!user || !isAllowed)) {
      navigate('/dashboard')
    }
  }, [user, authLoading, isAllowed, navigate])

  // Stats
  const [stats, setStats] = useState<DarkWebStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  // Dumps
  const [dumps, setDumps] = useState<DumpSiteResponse | null>(null)
  const [dumpsLoading, setDumpsLoading] = useState(false)
  const [dumpCat, setDumpCat] = useState('')

  // Subject monitor
  const [mName, setMName] = useState('')
  const [mEmail, setMEmail] = useState('')
  const [mAliases, setMAliases] = useState('')
  const [monitoring, setMonitoring] = useState(false)
  const [monitorResult, setMonitorResult] = useState<SubjectMonitorResponse | null>(null)

  // Search
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<{ query: string; total_hits: number; results: SearchResult[] } | null>(null)

  // Lumen
  const [lumenQuery, setLumenQuery] = useState('')
  const [lumenType, setLumenType] = useState('')
  const [lumenSearching, setLumenSearching] = useState(false)
  const [lumenResults, setLumenResults] = useState<LumenSearchResponse | null>(null)
  const [lumenStats, setLumenStats] = useState<LumenStatsResponse | null>(null)
  const [lumenStatsLoading, setLumenStatsLoading] = useState(false)

  async function fetchStats() {
    try {
      const r = await api.get('/darkweb/stats')
      setStats(r.data)
    } catch { /* ES offline */ }
    finally { setStatsLoading(false) }
  }

  async function fetchDumps(cat = '') {
    setDumpsLoading(true)
    try {
      const params: Record<string, string | number> = { size: 80 }
      if (cat) params.category = cat
      const r = await api.get('/darkweb/dumps', { params })
      setDumps(r.data)
    } catch { /* noop */ }
    finally { setDumpsLoading(false) }
  }

  useEffect(() => {
    fetchStats()
    intervalRef.current = setInterval(fetchStats, 15000)
    return () => clearInterval(intervalRef.current)
  }, [])

  useEffect(() => {
    if (tab === 'dumps' && !dumps) fetchDumps()
    if (tab === 'lumen' && !lumenStats) fetchLumenStats()
  }, [tab])

  async function handleMonitor(e: React.FormEvent) {
    e.preventDefault()
    if (!mName.trim()) return
    setMonitoring(true)
    setMonitorResult(null)
    try {
      const r = await api.post('/darkweb/monitor-subject', null, {
        params: {
          name: mName.trim(),
          ...(mEmail ? { email: mEmail.trim() } : {}),
          ...(mAliases ? { aliases: mAliases.trim() } : {}),
        }
      })
      setMonitorResult(r.data)
    } catch { /* noop */ }
    finally { setMonitoring(false) }
  }

  async function fetchLumenStats() {
    setLumenStatsLoading(true)
    try {
      const r = await api.get('/darkweb/lumen/stats')
      setLumenStats(r.data)
    } catch { /* ES offline or no lumen index */ }
    finally { setLumenStatsLoading(false) }
  }

  async function handleLumenSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!lumenQuery.trim()) return
    setLumenSearching(true)
    setLumenResults(null)
    try {
      const params: Record<string, string | number> = { q: lumenQuery, size: 30 }
      if (lumenType) params.notice_type = lumenType
      const r = await api.get('/darkweb/lumen/search', { params })
      setLumenResults(r.data)
    } catch { /* noop */ }
    finally { setLumenSearching(false) }
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    setSearchResults(null)
    try {
      const r = await api.get('/darkweb/search', { params: { q: query, size: 30 } })
      setSearchResults(r.data)
    } catch { /* noop */ }
    finally { setSearching(false) }
  }

  if (authLoading) return null
  if (!user || !isAllowed) return null

  const TABS: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Index Overview' },
    { key: 'dumps',    label: 'Dump Sites' },
    { key: 'monitor',  label: 'Subject Monitor' },
    { key: 'search',   label: 'Full-Text Search' },
    { key: 'lumen',    label: 'Lumen Takedowns' },
  ]

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top, #00100a 0%, #080810 70%)' }}>
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-6 mt-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs font-mono font-bold text-green-400 uppercase tracking-widest">
                  COLLECTION NODE — LIVE
                </span>
                <span className="text-xs font-mono text-dark-600 border border-dark-700 rounded px-2 py-0.5 ml-2">
                  @{ALLOWED_DOMAIN} ONLY
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight">
                Tor / Dark Web Index <span className="text-green-400">Operations</span>
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dark-search')}
                className="flex items-center gap-2 text-xs font-mono font-bold text-ginger-400 border border-ginger-500/40
                           hover:border-ginger-500/70 hover:text-ginger-300 px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Open Search
              </button>
              {stats && (
                <div className="text-right">
                  <div className="text-2xl font-black text-green-400 tabular-nums">{stats.total_indexed.toLocaleString()}</div>
                  <div className="text-xs text-dark-500 uppercase tracking-widest">.onion pages indexed</div>
                </div>
              )}
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 mb-6 bg-dark-900 border border-dark-700/50 rounded-xl p-1">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                  tab === t.key ? 'bg-dark-700 text-dark-100' : 'text-dark-500 hover:text-dark-300'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW ─── */}
          {tab === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {statsLoading && (
                <div className="flex items-center justify-center py-24">
                  <div className="w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-dark-400 font-mono text-sm">Connecting to index...</span>
                </div>
              )}
              {stats && (
                <div className="grid lg:grid-cols-[1fr_300px] gap-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-px bg-dark-700/30 rounded-xl overflow-hidden border border-dark-700/40">
                      {[
                        { val: stats.total_indexed.toLocaleString(), label: 'Pages Indexed', color: 'text-green-400' },
                        { val: stats.collector.crawl_count.toString(), label: 'Crawl Runs', color: 'text-ginger-400' },
                        { val: stats.collector.crawler_active ? 'ACTIVE' : 'IDLE', label: 'Crawler', color: stats.collector.crawler_active ? 'text-green-400' : 'text-yellow-400' },
                      ].map(({ val, label, color }) => (
                        <div key={label} className="bg-dark-900 p-5 text-center">
                          <div className={`text-3xl font-black ${color} tabular-nums`}>{val}</div>
                          <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">{label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Recent feed */}
                    <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        <h2 className="text-sm font-bold text-dark-200 uppercase tracking-wider">Recently Indexed</h2>
                        <span className="ml-auto text-xs text-dark-600 font-mono">auto-refresh 15s</span>
                      </div>
                      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                        {stats.recent_pages.map((page, i) => (
                          <div key={i} className="bg-dark-800/60 border border-dark-700/50 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-dark-100 text-sm font-medium leading-tight line-clamp-1">{page.title || '(no title)'}</span>
                              <span className="text-xs text-dark-600 font-mono flex-shrink-0">{page.updated_on ? new Date(page.updated_on).toLocaleTimeString() : ''}</span>
                            </div>
                            <div className="text-xs font-mono text-green-400/60 truncate">{page.domain}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right sidebar */}
                  <div className="space-y-4">
                    <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-5">
                      <h2 className="text-sm font-bold text-dark-200 uppercase tracking-wider mb-4">Collector Daemon</h2>
                      {[
                        { label: 'Last Crawl', value: stats.collector.last_crawl ? new Date(stats.collector.last_crawl).toLocaleString() : 'Never' },
                        { label: 'Next Crawl', value: stats.collector.next_crawl || '—' },
                        { label: 'Last Lumen', value: stats.collector.last_lumen_sweep ? new Date(stats.collector.last_lumen_sweep).toLocaleString() : 'Never' },
                        { label: 'Next Lumen', value: stats.collector.next_lumen || '—' },
                        { label: 'Crawl Runs', value: stats.collector.crawl_count.toString() },
                        { label: 'Lumen Sweeps', value: stats.collector.lumen_count.toString() },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-center py-1.5 border-b border-dark-800 last:border-0">
                          <span className="text-xs text-dark-500">{label}</span>
                          <span className="text-xs font-mono text-dark-200">{value}</span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-5">
                      <h2 className="text-sm font-bold text-dark-200 uppercase tracking-wider mb-3">Indices</h2>
                      {stats.indices.map(idx => (
                        <div key={idx.index_name} className="mb-3 last:mb-0">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-mono text-green-400 truncate">{idx.index_name}</span>
                            <span className="text-dark-400 ml-2 flex-shrink-0">{idx.doc_count.toLocaleString()} · {idx.size_mb}MB</span>
                          </div>
                          <div className="w-full h-1.5 bg-dark-800 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.min((idx.doc_count / Math.max(...stats.indices.map(i => i.doc_count), 1)) * 100, 100)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ── DUMPS ─── */}
          {tab === 'dumps' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div>
                  <h2 className="font-bold text-dark-100">Dump Site Catalog</h2>
                  <p className="text-dark-500 text-xs">Indexed .onion dump/breach repositories. Classified by type.</p>
                </div>
                <div className="ml-auto flex gap-1.5 flex-wrap">
                  {['', 'credential_dump', 'pii_dump', 'stealer_logs', 'forum_market', 'paste_site'].map(cat => (
                    <button
                      key={cat || 'all'}
                      onClick={() => { setDumpCat(cat); fetchDumps(cat) }}
                      className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                        dumpCat === cat ? 'bg-ginger-500/20 border-ginger-500/50 text-ginger-400' : 'border-dark-700 text-dark-500 hover:border-dark-600'
                      }`}
                    >
                      {CATEGORY_LABELS[cat] || 'All'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category summary bar */}
              {dumps && (
                <div className="flex gap-2 mb-4 flex-wrap">
                  {Object.entries(dumps.category_counts).map(([cat, count]) => (
                    <div key={cat} className={`border rounded-lg px-3 py-2 text-center min-w-[90px] ${CATEGORY_STYLES[cat] || CATEGORY_STYLES.general}`}>
                      <div className="text-xl font-black">{count}</div>
                      <div className="text-xs opacity-70 mt-0.5">{CATEGORY_LABELS[cat] || cat}</div>
                    </div>
                  ))}
                  <div className="border border-dark-700 rounded-lg px-3 py-2 text-center min-w-[90px]">
                    <div className="text-xl font-black text-dark-100">{dumps.total_dump_sites.toLocaleString()}</div>
                    <div className="text-xs text-dark-500 mt-0.5">Total Matched</div>
                  </div>
                </div>
              )}

              {dumpsLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-dark-400 text-sm">Loading dump site catalog...</span>
                </div>
              )}

              {dumps && !dumpsLoading && (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {dumps.sites.map((site, i) => (
                    <motion.div
                      key={site.url + i}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.015, 0.3) }}
                      className="bg-dark-900 border border-dark-700/50 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-dark-100 text-sm leading-tight">{site.title}</div>
                          <div className="text-xs font-mono text-green-400/60 truncate mt-0.5">{site.domain}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <CategoryBadge cat={site.category} />
                          <span className="text-xs text-dark-600 font-mono">{site.relevance_score}</span>
                        </div>
                      </div>
                      {site.dump_signals.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {site.dump_signals.map(sig => (
                            <span key={sig} className="text-xs bg-dark-800 border border-dark-700 rounded px-2 py-0.5 text-dark-400 font-mono">{sig}</span>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* ── SUBJECT MONITOR ─── */}
          {tab === 'monitor' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="grid lg:grid-cols-[360px_1fr] gap-6">
                {/* Input */}
                <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <h2 className="font-bold text-dark-100">Subject Monitor</h2>
                  </div>
                  <p className="text-dark-500 text-xs mb-5 leading-relaxed">
                    Search {stats?.total_indexed.toLocaleString() || '20,000+'} indexed .onion pages for a subject's name, email, address, and other identifiers.
                    Surfaces any mentions in dump repos, paste sites, and carding forums.
                    Results show context snippets only — raw dump data is never stored or displayed.
                  </p>

                  <form onSubmit={handleMonitor} className="space-y-3">
                    <div>
                      <label className="text-xs text-dark-500 uppercase tracking-wider font-bold block mb-1.5">Full Legal Name *</label>
                      <input type="text" value={mName} onChange={e => setMName(e.target.value)}
                        placeholder="John A. Smith" className="input w-full font-mono" required />
                    </div>
                    <div>
                      <label className="text-xs text-dark-500 uppercase tracking-wider font-bold block mb-1.5">Email Address</label>
                      <input type="email" value={mEmail} onChange={e => setMEmail(e.target.value)}
                        placeholder="jsmith@company.com" className="input w-full font-mono" />
                    </div>
                    <div>
                      <label className="text-xs text-dark-500 uppercase tracking-wider font-bold block mb-1.5">
                        Additional Identifiers <span className="text-dark-600 font-normal">(comma-sep)</span>
                      </label>
                      <input type="text" value={mAliases} onChange={e => setMAliases(e.target.value)}
                        placeholder="address, screen name, phone..." className="input w-full font-mono" />
                    </div>
                    <button type="submit" disabled={monitoring || !mName.trim()} className="btn btn-primary w-full">
                      {monitoring ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Scanning {stats?.total_indexed.toLocaleString()} pages...
                        </span>
                      ) : 'Run Dark Web Monitor'}
                    </button>
                  </form>

                  <div className="mt-4 p-3 bg-dark-800/50 rounded-lg border border-dark-700">
                    <p className="text-xs text-dark-500 leading-relaxed">
                      <strong className="text-dark-400">Data handling:</strong> Subject identifiers are searched in-memory only.
                      Results are context snippets — never the full dump content.
                      No PII is persisted to disk during this operation.
                    </p>
                  </div>
                </div>

                {/* Results */}
                <div>
                  {!monitorResult && !monitoring && (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                      <div className="text-4xl mb-3 opacity-20">🔍</div>
                      <p className="text-dark-600 text-sm">Enter identifiers to scan the dark web index</p>
                    </div>
                  )}

                  {monitorResult && (
                    <div>
                      {/* Summary card */}
                      <div className={`rounded-xl border p-5 mb-4 ${
                        monitorResult.dump_hits > 0 ? 'bg-red-950/20 border-red-500/40' :
                        monitorResult.total_hits > 0 ? 'bg-yellow-950/20 border-yellow-500/40' :
                        'bg-green-950/20 border-green-500/30'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-black text-lg">
                              {monitorResult.dump_hits > 0
                                ? `⚠ ${monitorResult.dump_hits} DUMP SITE HIT${monitorResult.dump_hits !== 1 ? 'S' : ''}`
                                : monitorResult.total_hits > 0
                                ? `${monitorResult.total_hits} PAGE MATCHES`
                                : '✓ NO DARK WEB EXPOSURE DETECTED'}
                            </div>
                            <div className="text-xs text-dark-400 mt-1">
                              Searched {monitorResult.sites_searched.toLocaleString()} indexed pages for "{monitorResult.subject}"
                            </div>
                          </div>
                          <div className="text-right text-xs text-dark-500">
                            <div>Identifiers checked: {monitorResult.identifiers_searched.length}</div>
                            <div>Total page matches: {monitorResult.total_hits}</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-dark-700">
                          {monitorResult.identifiers_searched.map(id => (
                            <span key={id} className="text-xs font-mono bg-dark-800 border border-dark-700 rounded px-2 py-0.5 text-dark-300">{id}</span>
                          ))}
                        </div>
                      </div>

                      {/* HIBP Breach Results */}
                      {monitorResult.breach_results && monitorResult.breach_results.length > 0 && (
                        <div className="mb-5">
                          <div className="text-xs font-mono font-bold text-dark-500 uppercase tracking-widest mb-2">
                            Have I Been Pwned — Email Breach Check
                          </div>
                          <div className="space-y-3">
                            {monitorResult.breach_results.map((er) => (
                              <div key={er.email} className={`rounded-xl border p-4 ${
                                er.has_password_exposure ? 'border-red-500/50 bg-red-950/20' :
                                er.breach_count > 0 ? 'border-orange-500/40 bg-orange-950/10' :
                                'border-green-500/30 bg-green-950/10'
                              }`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-mono text-sm text-white">{er.email}</span>
                                  <div className="flex items-center gap-2">
                                    {er.has_password_exposure && (
                                      <span className="text-xs font-bold text-red-400 bg-red-950/40 border border-red-500/40 px-2 py-0.5 rounded">PASSWORD EXPOSED</span>
                                    )}
                                    {er.has_pii_exposure && (
                                      <span className="text-xs font-bold text-orange-400 bg-orange-950/40 border border-orange-500/40 px-2 py-0.5 rounded">PII EXPOSED</span>
                                    )}
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${
                                      er.breach_count === 0 ? 'text-green-400 border-green-500/40' : 'text-red-300 border-red-500/30'
                                    }`}>
                                      {er.breach_count} breach{er.breach_count !== 1 ? 'es' : ''}
                                    </span>
                                    {er.risk_score > 0 && (
                                      <span className="text-xs font-mono text-dark-400">risk: {er.risk_score}/100</span>
                                    )}
                                  </div>
                                </div>
                                {er.error && (
                                  <p className="text-xs text-red-400 font-mono">{er.error}</p>
                                )}
                                {er.breaches.length > 0 && (
                                  <div className="space-y-1.5 mt-2">
                                    {er.breaches.map((b, bi) => (
                                      <div key={bi} className="flex items-start gap-2 text-xs font-mono">
                                        <span className={`shrink-0 px-1.5 py-0.5 rounded border font-bold ${
                                          b.severity === 'critical' ? 'text-red-400 border-red-500/40 bg-red-950/30' :
                                          b.severity === 'high' ? 'text-orange-400 border-orange-500/40 bg-orange-950/20' :
                                          'text-yellow-400 border-yellow-500/30 bg-yellow-950/10'
                                        }`}>{b.severity.toUpperCase()}</span>
                                        <span className="text-white font-bold">{b.name}</span>
                                        <span className="text-dark-500">{b.date}</span>
                                        <span className="text-dark-400 truncate">{b.data_exposed.join(', ')}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {er.paste_count > 0 && (
                                  <p className="text-xs text-orange-400 mt-2">+ {er.paste_count} paste site exposure{er.paste_count !== 1 ? 's' : ''}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tor Index Hit list */}
                      <div className="text-xs font-mono font-bold text-dark-500 uppercase tracking-widest mb-2">
                        Tor / Dark Web Page Matches
                      </div>
                      <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                        {monitorResult.results.map((hit, i) => (
                          <motion.div
                            key={hit.url + i}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04 }}
                            className="bg-dark-900 border border-dark-700/50 rounded-xl p-4"
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="font-semibold text-dark-100 text-sm">{hit.title}</div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded border ${CONFIDENCE_STYLES[hit.confidence]}`}>{hit.confidence}</span>
                                <CategoryBadge cat={hit.category} />
                              </div>
                            </div>
                            <div className="text-xs font-mono text-green-400/60 truncate mb-2">{hit.domain}</div>
                            {hit.snippet && (
                              <div
                                className="bg-dark-800 rounded-lg p-3 text-xs text-dark-300 leading-relaxed border-l-2 border-ginger-500/50"
                                dangerouslySetInnerHTML={{
                                  __html: hit.snippet
                                    .replace(/\[MATCH\]/g, '<strong class="text-red-400">')
                                    .replace(/\[\/MATCH\]/g, '</strong>')
                                }}
                              />
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-dark-600 font-mono">
                                matched: <span className="text-ginger-500">{hit.identifier_matched}</span>
                              </span>
                              <span className="text-xs text-dark-600">score: {hit.relevance_score}</span>
                            </div>
                          </motion.div>
                        ))}
                        {monitorResult.results.length === 0 && (
                          <div className="text-center py-8 text-dark-500 text-sm">No content matches in indexed pages</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* ── SEARCH ─── */}
          {tab === 'search' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <form onSubmit={handleSearch} className="flex gap-2 mb-5">
                <input
                  type="text" value={query} onChange={e => setQuery(e.target.value)}
                  placeholder='Search 20k+ indexed .onion pages — try "fullz", "credentials", "combo list", "dump"...'
                  className="input flex-1 font-mono"
                />
                <button type="submit" disabled={searching} className="btn btn-primary px-6">
                  {searching ? '...' : 'Search'}
                </button>
              </form>

              {searching && (
                <div className="flex items-center gap-2 text-dark-400 text-sm mb-4">
                  <div className="w-4 h-4 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin" />
                  Searching {stats?.total_indexed.toLocaleString()} indexed pages...
                </div>
              )}

              {searchResults && (
                <div>
                  <div className="text-xs text-dark-500 font-mono mb-4">
                    {searchResults.total_hits.toLocaleString()} results for "{searchResults.query}"
                  </div>
                  <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                    {searchResults.results.map((r, i) => (
                      <motion.div
                        key={r.url + i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="bg-dark-900 border border-dark-700/50 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div className="font-semibold text-dark-100 text-sm">{r.title || '(no title)'}</div>
                          <span className="text-xs text-ginger-500 font-mono flex-shrink-0">{r.score.toFixed(1)}</span>
                        </div>
                        <div className="text-xs font-mono text-green-400/60 truncate mb-2">{r.domain}</div>
                        {r.snippet && (
                          <p className="text-xs text-dark-400 leading-relaxed line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: r.snippet }} />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ── LUMEN TAKEDOWNS ─── */}
          {tab === 'lumen' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {/* Lumen stats bar */}
              {lumenStatsLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-dark-400 text-sm">Connecting to Lumen index...</span>
                </div>
              )}

              {lumenStats && (
                <div className="mb-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-4 text-center">
                      <div className="text-2xl font-black text-ginger-400 tabular-nums">{lumenStats.total_notices.toLocaleString()}</div>
                      <div className="text-xs text-dark-500 uppercase tracking-widest mt-1">Notices Indexed</div>
                    </div>
                    {Object.entries(lumenStats.by_type).slice(0, 3).map(([type, count]) => (
                      <div key={type} className={`rounded-xl border p-4 text-center ${NOTICE_TYPE_STYLES[type] || 'bg-dark-900 border-dark-700/50'}`}>
                        <div className="text-2xl font-black tabular-nums">{count.toLocaleString()}</div>
                        <div className="text-xs opacity-70 uppercase tracking-widest mt-1">{type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search bar */}
              <form onSubmit={handleLumenSearch} className="flex gap-2 mb-5">
                <input
                  type="text" value={lumenQuery} onChange={e => setLumenQuery(e.target.value)}
                  placeholder='Search takedown notices — try a name, company, domain, or keyword...'
                  className="input flex-1 font-mono"
                />
                <select
                  value={lumenType} onChange={e => setLumenType(e.target.value)}
                  className="input w-48 text-sm"
                >
                  <option value="">All Types</option>
                  <option value="Defamation">Defamation</option>
                  <option value="PrivateInformation">Private Info</option>
                  <option value="CourtOrder">Court Order</option>
                  <option value="GovernmentRequest">Gov Request</option>
                  <option value="DMCA">DMCA</option>
                  <option value="Trademark">Trademark</option>
                </select>
                <button type="submit" disabled={lumenSearching} className="btn btn-primary px-6">
                  {lumenSearching ? '...' : 'Search'}
                </button>
              </form>

              {lumenSearching && (
                <div className="flex items-center gap-2 text-dark-400 text-sm mb-4">
                  <div className="w-4 h-4 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin" />
                  Searching {lumenStats?.total_notices.toLocaleString() || ''} takedown notices...
                </div>
              )}

              {/* Search results */}
              {lumenResults && (
                <div>
                  <div className="text-xs text-dark-500 font-mono mb-4">
                    {lumenResults.total_hits.toLocaleString()} results for "{lumenResults.query}"
                  </div>
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {lumenResults.results.map((notice, i) => (
                      <motion.div
                        key={notice.notice_id + '-' + i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(i * 0.03, 0.3) }}
                        className="bg-dark-900 border border-dark-700/50 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-dark-100 text-sm leading-tight">{notice.title}</div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-dark-500">
                              <span>From: <span className="text-dark-300">{notice.sender_name}</span></span>
                              <span>To: <span className="text-dark-300">{notice.recipient_name}</span></span>
                              {notice.date_received && (
                                <span className="font-mono">{new Date(notice.date_received).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded border ${NOTICE_TYPE_STYLES[notice.notice_type] || 'text-dark-400 bg-dark-800 border-dark-700'}`}>
                              {notice.notice_type}
                            </span>
                            {notice.url_count > 0 && (
                              <span className="text-xs text-dark-600 font-mono">{notice.url_count} URLs</span>
                            )}
                          </div>
                        </div>

                        {notice.snippet && (
                          <div
                            className="bg-dark-800 rounded-lg p-3 text-xs text-dark-300 leading-relaxed border-l-2 border-ginger-500/50 mt-2"
                            dangerouslySetInnerHTML={{ __html: notice.snippet }}
                          />
                        )}

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex flex-wrap gap-1">
                            {notice.topics.map(t => (
                              <span key={t} className="text-xs bg-dark-800 border border-dark-700 rounded px-2 py-0.5 text-dark-400">{t}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-dark-600">
                            <span>Action: <span className={notice.action_taken === 'Yes' ? 'text-green-400' : 'text-red-400'}>{notice.action_taken}</span></span>
                            <span className="font-mono">#{notice.notice_id}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                    {lumenResults.results.length === 0 && (
                      <div className="text-center py-8 text-dark-500 text-sm">No matching takedown notices</div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent notices (shown when no search active) */}
              {!lumenResults && !lumenSearching && lumenStats && lumenStats.recent_notices.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-dark-300 uppercase tracking-wider mb-3">Recent Takedown Notices</h3>
                  <div className="space-y-2">
                    {lumenStats.recent_notices.map((notice, i) => (
                      <div key={notice.notice_id + '-recent-' + i} className="bg-dark-900 border border-dark-700/50 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-dark-100 text-sm">{notice.title}</div>
                            <div className="text-xs text-dark-500 mt-1">
                              {notice.sender_name} → {notice.recipient_name}
                              {notice.date_received && <span className="ml-2 font-mono">{new Date(notice.date_received).toLocaleDateString()}</span>}
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${NOTICE_TYPE_STYLES[notice.notice_type] || 'text-dark-400 bg-dark-800 border-dark-700'}`}>
                            {notice.notice_type}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

        </div>
      </div>
    </div>
  )
}
